import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Grid,
  MenuItem,
  Typography,
  useTheme,
} from '@mui/material';
import SpectrumTextField from '../../components/Spectrum/TextField/SpectrumTextField';
import SpectrumSelectField from '../../components/Spectrum/Select/SpectrumSelect';
import { accountTypes } from '../../utils/paymentUtils';
import PaymentBillingAddress from './PaymentBillingAddress';
import { isZipValid } from '../../utils/validation';
import {
  INewPaymentBankAccountDetails,
  INewPaymentBankAccountErrorDetails,
  INewPaymentBankDetailsPayload,
} from '../../types';

interface IBankAccountPaymentProps {
  emitHeight: () => void;
  isMobileView: boolean;
  handleAddPaymentMethod: (data: INewPaymentBankDetailsPayload) => void;
  addPaymentLoading: boolean;
  addPaymentError: string;
}

const BankAccountPayment = ({
  isMobileView,
  emitHeight,
  handleAddPaymentMethod,
  addPaymentError,
  addPaymentLoading,
}: IBankAccountPaymentProps) => {
  const theme = useTheme();
  const [accountDetails, setAccountDetails] = useState<INewPaymentBankAccountDetails>({
    accountNumber: '',
    confirmAccountNumber: '',
    routingNumber: '',
    confirmRoutingNumber: '',
    customerState: '',
    accountType: '',
  } as INewPaymentBankAccountDetails);
  const [errorDetails, setErrorDetails] = useState<INewPaymentBankAccountErrorDetails>(
    {} as INewPaymentBankAccountErrorDetails,
  );
  const validateFields = (
    {
      accountNumber,
      confirmAccountNumber,
      routingNumber,
      confirmRoutingNumber,
      accountType,
      customerName,
      customerAddress1,
      customerCity,
      customerZIP,
      customerState,
    }: INewPaymentBankAccountDetails,
    errorDetails: INewPaymentBankAccountErrorDetails,
  ) => {
    let valid = true;
    const errors = { ...errorDetails };
    if (!accountNumber) {
      valid = false;
      errors.accountNumber = 'Please enter account number';
    }
    if (!confirmAccountNumber) {
      valid = false;
      errors.confirmAccountNumber = 'Please confirm account number';
    }
    if (accountNumber !== confirmAccountNumber) {
      valid = false;
      errors.confirmAccountNumber = 'Account number and Confirm Account Number not matched';
    }
    if (!routingNumber) {
      valid = false;
      errors.routingNumber = 'Please enter routing number';
    }
    if (!confirmRoutingNumber) {
      valid = false;
      errors.confirmRoutingNumber = 'Please confirm routing number';
    }
    if (routingNumber !== confirmRoutingNumber) {
      valid = false;
      errors.confirmRoutingNumber = 'Routing number and Confirm Routing number not matched';
    }
    if (!accountType) {
      valid = false;
      errors.accountType = 'Please select account type';
    }
    if (!customerName) {
      valid = false;
      errors.customerName = 'Please enter name';
    }
    if (!customerAddress1) {
      valid = false;
      errors.customerAddress1 = 'Please enter address line 1';
    }
    if (!customerZIP) {
      valid = false;
      errors.customerZIP = 'Please enter zip code';
    }
    if (customerZIP && !isZipValid(customerZIP)) {
      valid = false;
      errors.customerZIP = 'Please enter valid zip code';
    }
    if (!customerState) {
      valid = false;
      errors.customerState = 'Please select state';
    }
    if (!customerCity) {
      valid = false;
      errors.customerCity = 'Please enter city';
    }
    return { valid, errors };
  };
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const name = event.target.name;
    let value: string | boolean = event.target.value;
    if (name === 'isDefault' || name === 'saveToWallet') {
      value = event.target.checked;
    }
    if (
      name === 'accountNumber' ||
      name === 'confirmAccountNumber' ||
      name === 'routingNumber' ||
      name === 'confirmRoutingNumber' ||
      name === 'cardholderZip'
    ) {
      value = String(value).replace(/ /g, '');
    }
    setErrorDetails({ ...errorDetails, [name]: '' });
    setAccountDetails({ ...accountDetails, [name]: value });
  };
  const handleContinue = () => {
    const { valid, errors } = validateFields(accountDetails, errorDetails);
    if (!valid) {
      setErrorDetails(errors);
      return;
    }
    const {
      accountType,
      accountNumber,
      routingNumber,
      customerName,
      customerAddress1,
      customerAddress2,
      customerCity,
      customerState,
      customerZIP,
    } = accountDetails;
    const payload: INewPaymentBankDetailsPayload = {
      customerName,
      customerAddress1,
      customerAddress2: customerAddress2 || '',
      customerCity,
      customerState,
      customerZIP,
      routingNumber,
      accountNumber,
      accountType,
      customerPhone: '',
      customerCountryCode: '',
      paymentMethodSuffix: accountNumber.slice(accountNumber.length - 4),
    };
    handleAddPaymentMethod(payload);
  };

  useEffect(() => {
    emitHeight();
  }, [JSON.stringify(errorDetails)]);

  return (
    <div>
      <Typography variant='h4' sx={{ mt: 8, mb: 10 }}>
        Bank Account Details
      </Typography>
      <form autoComplete='off'>
        <Grid container spacing={8}>
          <Grid item mobile={12} width='100%'>
            <SpectrumTextField
              required
              label='Account Number'
              variant='outlined'
              size='small'
              name='accountNumber'
              onChange={handleChange}
              value={accountDetails?.accountNumber}
              error={!!errorDetails?.accountNumber}
              helperText={errorDetails?.accountNumber}
            />
          </Grid>
          <Grid item mobile={12} width='100%'>
            <SpectrumTextField
              required
              label='Confirm Account Number'
              variant='outlined'
              size='small'
              name='confirmAccountNumber'
              onChange={handleChange}
              value={accountDetails?.confirmAccountNumber}
              error={!!errorDetails?.confirmAccountNumber}
              helperText={errorDetails?.confirmAccountNumber}
            />
          </Grid>
          <Grid item mobile={12} width='100%'>
            <SpectrumTextField
              required
              label='Routing Number'
              variant='outlined'
              size='small'
              name='routingNumber'
              onChange={handleChange}
              value={accountDetails?.routingNumber}
              error={!!errorDetails?.routingNumber}
              helperText={errorDetails?.routingNumber}
            />
          </Grid>
          <Grid item mobile={12} width='100%'>
            <SpectrumTextField
              required
              label='Confirm Routing Number'
              variant='outlined'
              size='small'
              name='confirmRoutingNumber'
              onChange={handleChange}
              value={accountDetails?.confirmRoutingNumber}
              error={!!errorDetails?.confirmRoutingNumber}
              helperText={errorDetails?.confirmRoutingNumber}
            />
          </Grid>
          <Grid item mobile={12} width='100%'>
            <SpectrumSelectField
              required
              label='Account Type'
              variant='outlined'
              size='small'
              name='accountType'
              onChange={handleChange}
              value={accountDetails?.accountType}
              error={!!errorDetails?.accountType}
              helperText={errorDetails?.accountType}
            >
              {accountTypes.map((item) => (
                <MenuItem key={item?.value} value={item?.value}>
                  {item?.label}
                </MenuItem>
              ))}
            </SpectrumSelectField>
          </Grid>
        </Grid>
      </form>
      <PaymentBillingAddress
        handleChange={handleChange}
        details={accountDetails}
        errorDetails={errorDetails}
        isMobileView={isMobileView}
      />
      {addPaymentError && (
        <Typography textAlign='center' mt={10} mb={10} variant='paragraph2' color='error'>
          {addPaymentError}
        </Typography>
      )}
      <Button
        sx={{ mt: 12, mb: 4 }}
        variant='contained'
        size='large'
        fullWidth
        onClick={handleContinue}
      >
        {addPaymentLoading ? (
          <CircularProgress style={{ color: theme.palette.common.white }} />
        ) : (
          'Continue'
        )}
      </Button>
    </div>
  );
};
export default BankAccountPayment;
